<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      ref="tableRemoteRef"
      :items="displayedWorkOrdersTemplate"
      :title="$t('workOrdersTemplate.pageName')"
      :iconHeader="hasPermission('workOrders.templates.add') ? 'add' : null"
      :isSelectable="hasPermission('workOrders.view')"
      :headers="tableHeaders"
      :pagination="pagination"
      :itemActions="actions"
      :isWorkOrderTemplate="true"
      :displayeFilter="false"
      :isInfinitScroll="true"
      :isSearchable="false"
      :selectedType="selectedType"
      :centerColumns="['billable']"
      :endColumns="['action']"
      :sortableColumns="sortableColumns"
      :notSortableColumns="['billable']"
      hideDefaultFooter
      @iconAction="onClickAction"
      @start-workOrder="startWO"
      @sendQueryParams="fetchQuery"
    >
      <template v-slot:[`item.billable`]="{ item }">
        <VCheckbox
          v-model="item.billable"
          class="d-flex justify-center"
          dense
          color="primary"
          :readonly="true"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('laboratory.placeholder.searchBy')"
          :filterName="filterName"
          @onApplyFilters="templateTypeChanged"
          @onSearch="search"
        />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import roles from '@/constants/roles';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import WorkOrderTemplateType from '@/components/forms/workOrder/WorkOrderTemplateType';
import { mapGetters, mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import perpage from '@/constants/pagination';


export default {
    components: {
        TableRemote,
        VCheckbox,
        SearchWithFilters,
    },
    data() {
        return {
            workOrdersTemplates: [],
            loading: false,
            pagination: {},
            selectedType: '',
            filterName: 'workOrdersTemplate',
            queryParams: {
                perpage,
                site: this.$route.params.siteId,
                type: null,
            },
            site: this.$route.params.siteId,
            initializing: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'hasRole',
            'lang',
            'member',
        ]),
        roles() {
            return roles;
        },
        filters() {
            return [
                {
                    key: 'type',
                    label: 'laboratory.labels.type',
                    type: 'select',
                    multiple: true,
                    items: [
                        { text: this.$t('forms.labels.workOrderTemplate.Team'), value: 'Team', isAlreadyTranslated: true },
                        { text: this.$t('forms.labels.workOrderTemplate.Personal'), value: 'Personal', isAlreadyTranslated: true },
                    ],
                },
            ];
        },
        headers() {
            const mobileHeaders = [
                'title',
                'woType',
                'type',
            ];

            const headers = [
                'title',
                'woType',
                'type',
                'createdBy',
                'billable',
            ];

            return this.$vuetify.breakpoint.mdAndDown ? mobileHeaders : headers;
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`workOrdersTemplate.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
        displayedWorkOrdersTemplate() {
            return this.workOrdersTemplates.map(woTemplate => (this.formatWOTemplate(woTemplate)));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('workOrders.templates.edit')) {
                actions.push({
                    name: this.$t('workOrdersTemplate.labels.edit'),
                    icon: 'edit',
                });
            }
            if (this.hasPermission('workOrders.templates.add')) {
                actions.push({
                    name: this.$t('workOrdersTemplate.labels.duplicate'),
                    icon: 'content_copy',
                });
            }
            if (this.hasPermission('workOrders.templates.remove')) {
                actions.push({
                    name: this.$t('workOrdersTemplate.labels.delete'),
                    icon: 'delete',
                    iconAndTextColor: 'red',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        this.getWOTemplates();
        this.loading = false;
    },

    beforeRouteLeave(to, from, next) {
        if (to === 'siteEditTemplate' && from === 'siteWorkOrdersTemplate') {
            this.resetWOTemplatesInformations();
        }
        next();
    },

    methods: {
        ...mapActions([
            'resetWOTemplateInfo',
            'getWOTemplateInfo',
        ]),
        async search(search, changeType = false) {
            if (!changeType) this.queryParams.search = search;
            this.queryParams = { ...this.queryParams, page: 1 };
            this.pagination = {};
            this.workOrdersTemplates = [];
            const { data } = await this.$rq.getWOTemplates(this.queryParams);
            this.workOrdersTemplates = [...this.workOrdersTemplates, ...data.woTemplates];
            this.pagination = data.pagination;
        },
        async getWOTemplates() {
            if (!this.initializing && (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page))) {
                this.initializing = true;
                const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
                const { data } = await this.$rq.getWOTemplates(cleanQueryParams);
                this.workOrdersTemplates = [...this.workOrdersTemplates, ...data.woTemplates];
                this.pagination = data.pagination;
                this.initializing = false;
            }
        },
        async templateTypeChanged(queryParams) {
            if (queryParams.reset) {
                await this.resetQueryParams();
            } else {
                this.queryParams.type = queryParams.type;
            }

            await this.getWOTemplates();
        },
        formatWOTemplate(woTemplate) {
            return {
                title: woTemplate.title,
                woType: woTemplate.workOrderType.name[this.lang],
                type: woTemplate.templateType,
                createdBy: `${woTemplate.createdBy?.firstName} ${woTemplate.createdBy?.lastName}`,
                createdUserId: woTemplate.createdBy.id,
                billable: woTemplate.billable,
                id: woTemplate.id,
                poNumber: woTemplate.poNumber,
                soNumber: woTemplate.soNumber,
                networks: woTemplate.networks,
                openingComment: woTemplate.openingComment,
                closingComment: woTemplate.closingComment,
                commentComment: woTemplate.commentComment,
                report: woTemplate.report,
                summaryReport: woTemplate.summaryReport,
                team: woTemplate.team,
                site: woTemplate.site ? woTemplate.site.id : '',
            };
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.$router.push({
                    name: this.site ? 'siteCreateTemplate' : 'createTemplate',
                    params: {
                        isEdition: true,
                    },
                });
                return;
            }
            switch (action.icon) {
                case 'edit':
                    this.editWorOrderTemplate(item);
                    break;
                case 'content_copy':
                    this.duplicateWorkOrderTemplate(item);
                    break;
                case 'delete':
                    this.removeWorkOrderTemplate(item);
                    break;
                default:
                    break;
            }
        },
        async duplicateWorkOrderTemplate(template) {
            this.$root.$emit('showModal', {
                title: this.$t('workOrdersTemplate.workOrderTemplateType'),
                buttonTitle: 'confirm',
                clean: true,
                payload: { isSite: true, isOnService: this.$route.matched[0].path === '/services' },
                component: Vue.component('WorkOrderTemplateType', WorkOrderTemplateType),
                onConfirm: async ({ value }) => {
                    const { data } = await this.$rq.duplicateWOTemplate(template.id, {
                        templateType: value,
                    });
                    this.editWorOrderTemplate(data);
                },
            });
        },
        async editWorOrderTemplate(template) {
            if (template.id) {
                await this.getWOTemplateInfo(template.id);
                this.$router.push({
                    name: this.site ? 'siteEditTemplate' : 'editTemplate',
                    params: {
                        woTemplateId: template.id,
                    },
                    query: { exp: Date.now() },
                });
            }
        },
        async removeWorkOrderTemplate(item) {
            this.$root.$emit('showModal', {
                title: this.$t('workOrdersTemplate.remove').toUpperCase(),
                component: Vue.component('DeleteForm', DeleteForm),
                buttonTitle: 'delete',
                payload: { msg: this.$t('workOrdersTemplate.msg.remove'), security: this.$t('workOrdersTemplate.msg.security') },
                isForm: true,
                onConfirm: async () => {
                    await this.$rq.deleteWorkOrderTemplate(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.workOrderDeletedTemplate', { data: item.contractNumber }),
                    });
                    this.workOrdersTemplates = [];
                    this.pagination = {};
                    return this.getWOTemplates(true);
                },
            });
        },
        async startWO(template) {
            this.loading = true;
            const req = {
                site: template.site.length ? null : this.site,
            };
            const { data } = await this.$rq.createWOFromTemplate(template.id, req);
            await this.$router.replace({
                name: 'WorkOrderView',
                params: {
                    workOrderId: data.id,
                    clientId: data.site.client.ref,
                    siteId: data.site.ref,
                },
            });
            this.loading = false;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getWOTemplates();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        async fetchQuery(queryParams) {
            if (!this.loading) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    perpage: queryParams?.perpage || queryParams.perpage,
                    search: queryParams?.search || '',
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.workOrdersTemplates = [];
                await this.getWOTemplates();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'title':
                    newValue = 'title';
                    break;
                case 'wotype':
                    newValue = 'workOrderType';
                    break;
                case 'type':
                    newValue = 'type';
                    break;
                case 'createdby':
                    newValue = 'createdby';
                    break;

                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },
        resetWOTemplatesInformations() {
            this.resetWOTemplateInfo();
        },
        async resetQueryParams() {
            this.pagination = {};
            this.queryParams = {
                perpage,
                site: this.$route.params.siteId,
                type: null,
                search: '',
                page: 1,
            };
        },
    },
};
</script>
